// src/pages/ProfilePage.jsx
import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "@/services/axios";
import { API } from "@/config/api";
import { Button } from "@/components/ui/button";
import {
  Loader2,
  Package,
  Download,
  Eye,
  RefreshCw,
  User,
  MapPin,
  LogOut,
} from "lucide-react";
import { toast } from "sonner";

export const ProfilePage = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("orders");

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }
    loadOrders();
  }, [isAuthenticated]);

  const loadOrders = async () => {
    try {
      const response = await axios.get(API.orders.list);
      setOrders(response.data);
    } catch (error) {
      console.error("Failed to load orders:", error);
      toast.error("Failed to load order history");
    } finally {
      setLoading(false);
    }
  };

  const handleReorder = async (orderId) => {
    try {
      await axios.post(API.orders.reorder(orderId));
      toast.success("Items added to cart");
      navigate("/cart");
    } catch (error) {
      toast.error("Failed to reorder");
    }
  };

  const downloadInvoice = async (orderId) => {
    try {
      const response = await axios.get(
        `${API.base}/api/v1/invoices/${orderId}`,
        {
          responseType: "blob",
        },
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `invoice-${orderId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error("Failed to download invoice");
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-24 px-6">
      <div className="max-w-6xl mx-auto">
        <h1 className="font-heading text-4xl font-bold mb-8">My Account</h1>

        <div className="grid md:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="md:col-span-1">
            <div className="bg-white border border-border rounded-md overflow-hidden sticky top-24">
              {/* Profile Summary */}
              <div className="p-6 bg-accent text-white text-center">
                <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-white">
                  <span className="text-2xl font-bold">
                    {user?.first_name?.[0]}
                    {user?.last_name?.[0]}
                  </span>
                </div>
                <h2 className="font-semibold text-lg">
                  {user?.first_name} {user?.last_name}
                </h2>
                <p className="text-sm text-white/80 mt-1">{user?.email}</p>
              </div>

              {/* Navigation */}
              <div className="p-4">
                <button
                  onClick={() => setActiveTab("orders")}
                  className={`w-full text-left px-4 py-3 rounded-sm transition-colors flex items-center gap-3 ${
                    activeTab === "orders"
                      ? "bg-accent text-white"
                      : "hover:bg-secondary"
                  }`}
                >
                  <Package className="w-4 h-4" />
                  <span>My Orders</span>
                </button>

                <button
                  onClick={() => setActiveTab("profile")}
                  className={`w-full text-left px-4 py-3 rounded-sm transition-colors flex items-center gap-3 mt-1 ${
                    activeTab === "profile"
                      ? "bg-accent text-white"
                      : "hover:bg-secondary"
                  }`}
                >
                  <User className="w-4 h-4" />
                  <span>Profile Details</span>
                </button>

                <button
                  onClick={() => setActiveTab("addresses")}
                  className={`w-full text-left px-4 py-3 rounded-sm transition-colors flex items-center gap-3 mt-1 ${
                    activeTab === "addresses"
                      ? "bg-accent text-white"
                      : "hover:bg-secondary"
                  }`}
                >
                  <MapPin className="w-4 h-4" />
                  <span>Saved Addresses</span>
                </button>

                <div className="border-t border-border my-4"></div>

                <button
                  onClick={logout}
                  className="w-full text-left px-4 py-3 rounded-sm transition-colors flex items-center gap-3 text-red-600 hover:bg-red-50"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Sign Out</span>
                </button>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="md:col-span-3">
            {activeTab === "orders" && (
              <div className="bg-white border border-border rounded-md p-6">
                <h2 className="font-heading text-2xl font-bold mb-6">
                  Order History
                </h2>

                {orders.length === 0 ? (
                  <div className="text-center py-12">
                    <Package className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                    <p className="text-muted-foreground mb-4">
                      You haven't placed any orders yet
                    </p>
                    <Button
                      onClick={() => navigate("/")}
                      className="bg-accent hover:bg-accent-hover text-white"
                    >
                      Start Shopping
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {orders.map((order) => (
                      <div
                        key={order.id}
                        className="border border-border rounded-md p-4 hover:border-accent transition-colors"
                      >
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <p className="text-sm text-muted-foreground">
                              Order #{order.id.slice(0, 8)}
                            </p>
                            <p className="text-sm">
                              Placed on{" "}
                              {new Date(order.created_at).toLocaleDateString(
                                "en-US",
                                {
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                },
                              )}
                            </p>
                          </div>
                          <div className="flex gap-2 flex-wrap justify-end">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              order.approval_status === "approved" ? "bg-green-100 text-green-800"
                              : order.approval_status === "declined" ? "bg-red-100 text-red-800"
                              : "bg-amber-100 text-amber-800"
                            }`}>
                              {order.approval_status === "approved" ? "Approved"
                               : order.approval_status === "declined" ? "Declined"
                               : "Under Review"}
                            </span>
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              order.payment_status === "paid" ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"
                            }`}>
                              {order.payment_status === "paid" ? "Paid" : "Payment Pending"}
                            </span>
                          </div>
                        </div>

                        <div className="space-y-2 mb-4">
                          {order.items.map((item, idx) => (
                            <div
                              key={idx}
                              className="flex justify-between text-sm"
                            >
                              <span>
                                {item.filename} ({item.metal_name}) x
                                {item.quantity}
                              </span>
                              <span className="font-mono">
                                ${item.line_total.toFixed(2)}
                              </span>
                            </div>
                          ))}
                        </div>

                        <div className="flex justify-between items-center pt-4 border-t border-border">
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/order/${order.id}`)}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Details
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => downloadInvoice(order.id)}
                            >
                              <Download className="w-4 h-4 mr-1" />
                              Invoice
                            </Button>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="font-semibold">Total:</span>
                            <span className="font-heading text-xl text-accent">
                              ${order.total.toFixed(2)}
                            </span>
                            <Button
                              size="sm"
                              onClick={() => handleReorder(order.id)}
                              className="bg-accent hover:bg-accent-hover text-white"
                            >
                              <RefreshCw className="w-4 h-4 mr-1" />
                              Reorder
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === "profile" && (
              <div className="bg-white border border-border rounded-md p-6">
                <h2 className="font-heading text-2xl font-bold mb-6">
                  Profile Details
                </h2>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-muted-foreground">
                        First Name
                      </label>
                      <p className="font-medium text-lg">{user?.first_name}</p>
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">
                        Last Name
                      </label>
                      <p className="font-medium text-lg">{user?.last_name}</p>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm text-muted-foreground">
                      Email
                    </label>
                    <p className="font-medium text-lg">{user?.email}</p>
                  </div>

                  <div>
                    <label className="text-sm text-muted-foreground">
                      Phone
                    </label>
                    <p className="font-medium text-lg">
                      {user?.phone || "Not provided"}
                    </p>
                  </div>

                  <div>
                    <label className="text-sm text-muted-foreground">
                      Member Since
                    </label>
                    <p className="font-medium text-lg">
                      {new Date(user?.created_at).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "addresses" && (
              <div className="bg-white border border-border rounded-md p-6">
                <h2 className="font-heading text-2xl font-bold mb-6">
                  Saved Addresses
                </h2>

                <div className="text-center py-12 border-2 border-dashed border-border rounded-md">
                  <MapPin className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground mb-4">
                    You haven't saved any addresses yet
                  </p>
                  <Button
                    variant="outline"
                    className="border-accent text-accent hover:bg-accent hover:text-white"
                  >
                    Add New Address
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
